import React from "react";
import { createStyles, Theme, makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Grid,
  Typography,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from "@material-ui/core";
import { useForm, FormContext, FieldError } from "react-hook-form";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { IIndianaStateBoardReport } from "../../../interfaces/setup/reports/IIndianaStateBoardReportProgramCode";
import InputField from "../../../components/_Layout/Inputs/InputField";
import { InputType } from "../../../constants/uiConstants/inputConstants";
import { cloneDeep } from "lodash";
import {
  getIndianaStateBoardReportSettings,
  upsertIndianaStateBoardReportProgramCode,
  deleteIndianaStateBoardReportSettings,
  getStateBoardAgencyDetail,
} from "../../../api/reports/stateBoard/stateBoardReportApi";
import ProgressSaveButton from "../../../components/_Layout/Buttons/ProgressSaveButton";
import CustomSnackbar from "../../../components/notifications/CustomSnackbar";
import { CustomSnackBarProps } from "../../../interfaces/common/CustomSnackBarProps";
import { IMichiganStateBoardSettings } from "../../../interfaces/setup/reports/IMichiganStateBoardSettings";

import CountryAutoComplete from "../../../components/AutoComplete/CountryAutoComplete";
import StateBoardCoursesAutoComplete from "../../../components/AutoComplete/StateBoardCoursesAutoComplete";
import StatesAutoComplete from "../../../components/AutoComplete/StatesAutoComplete";
import LicensingAgencyAutoComplete from "../../../components/AutoComplete/LicensingAgencyAutoComplete";
import AllStatesAutoComplete from "../../../components/AutoComplete/AllStatesAutoComplete";
import { EmptyGuid } from "../../../utils/constants";
import { IStateBoardAgencyDetail } from "../../../interfaces/setup/reports/IStateBoardAgencyDetail";
import { getProgramVersionsForStateboardSettings } from "../../../api/academicRecords/programVersionApi";

const useStyles = makeStyles((theme: any) =>
  createStyles({
    root: {
      width: "100%",
      height: "100%",
      minHeight: "100%",
      padding: theme.spacing(3),
      overflowX: "hidden",
      overflowY: "auto",
    },
    container: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      height: "100%",
    },
    details: {
      padding: theme.spacing(2, 0.5),
      flex: "1 1",
    },
    componentActions: {
      marginTop: theme.spacing(3),
    },
    wrapper: {
      height: "80%",
      padding: "3px",
      overflow: "auto",
    },
    form: {
      height: "100%",
    },
    titleFont: {
      background: "	rgb(220,220,220)",
      padding: "15px",
      textAlign: "left",
      fontFamily: "sans-serif",
    },
    headerFont: {
      background: "	rgb(243,240,239)",
    },
    btnDelete: {
      backgroundColor: theme.palette.error.dark,
      marginLeft: theme.spacing(1),
    },
    heading: {
      fontSize: theme.typography.pxToRem(15),
      fontWeight: theme.typography.fontWeightRegular,
    },
    cardTitle: {
      //fontSize: 24,
      color: theme.palette.black,
      textDecoration: "bold",
      backgroundColor: theme.palette.site.secondary,
    },
    titleMargin: {
      margin: theme.spacing(2),
      marginTop: theme.spacing(3),
    },
    licensingAgencyTitleMargin: {
      margin: theme.spacing(2),
    },
    fieldMargin: {
      marginTop: theme.spacing(1.5),
    },
  })
);

interface IProps {
  stateId: string;
  stateDescription: string;
  stateBoardReportId: string;
  stateBoardReportDescription: string;
  campusId: string;
}

const IndianaStateBoardReportSetup = (props: IProps) => {
  const classes = useStyles({});
  const data = useForm<any>({
    mode: "onBlur",
  });

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [openConfirmationPopup, setOpenConfirmationPopup] = React.useState<boolean>(false);

  const initialModel: IIndianaStateBoardReport = {
    campusCity: null,
    campusCountryId: null,
    campusId: props.campusId,
    campusStateId: null,
    campusZipCode: null,
    licensingAddress1: null,
    licensingAddress2: null,
    licensingAgencyId: null,
    licensingCity: null,
    licensingCountryId: null,
    licensingStateId: null,
    licensingZipCode: null,
    officerName1: null,
    officerName2: null,
    officerName3: null,
    ownerLicenseNumber: null,
    ownerName: null,
    reportId: props.stateBoardReportId,
    schoolAddress1: null,
    schoolAddress2: null,
    schoolCertification: null,
    schoolLicense: null,
    schoolName: null,
    schoolStateBoardReportId: null,
    stateId: null,
    studentRegistrationFee: null,
    stateBoardProgramMappings: null,
  };

  const [settings, setSettings] = React.useState<IIndianaStateBoardReport>(
    cloneDeep(initialModel)
  );

  const handleChange = (fieldName: any, fieldValue: any) => {
    let copy: any = cloneDeep(settings);
    copy[fieldName] = fieldValue;
    setSettings(copy);

    if (fieldName == "licensingAgencyId") {
      getAgencyDetails(fieldValue, copy);
    }
  };

  const getAgencyDetails = (
    agencyId: number,
    copy: IIndianaStateBoardReport
  ) => {
    getStateBoardAgencyDetail(agencyId)
      .then((res: IStateBoardAgencyDetail) => {
        if (!!res) {
          copy.licensingAddress1 = res.address1;
          copy.licensingAddress2 = res.address2;
          copy.licensingCountryId = res.countryId;
          copy.licensingCity = res.city;
          copy.licensingStateId = res.stateId;
          copy.licensingZipCode = res.zipCode;
          setSettings(copy);
        } else {
          copy.licensingAddress1 = null;
          copy.licensingAddress2 = null;
          copy.licensingCountryId = undefined;
          copy.licensingCity = null;
          copy.licensingStateId = null;
          copy.licensingZipCode = null;
          setSettings(copy);
        }
      })
      .catch((e) => {
        copy.licensingAddress1 = null;
        copy.licensingAddress2 = null;
        copy.licensingCountryId = undefined;
        copy.licensingCity = null;
        copy.licensingStateId = null;
        copy.licensingZipCode = null;
        setSettings(copy);
      });
  };

  const [snackBarProps, setSnackBarProps] = React.useState<CustomSnackBarProps>(
    {
      showSnackBar: false,
      messageInfo: undefined,
      variant: "info",
    }
  );

  const handleProgramCodeChange = (value: any, programId: string, reportId: string) => {
    setSettings((prevSettings) => {
        const updatedMappings = prevSettings.stateBoardProgramMappings?.map((mapping) => {
            if (mapping.programId === programId) {
                return { ...mapping, stateBoardCourseId: value, schoolStateBoardReportId: reportId };
            }
            return mapping;
        });

        return {
            ...prevSettings,
            stateBoardProgramMappings: updatedMappings, 
        };
    });
};

  const getStateBoardReportSettings = (
    reportId: string,
    stateId: string,
    campusId: string
  ) => {
    if(reportId != EmptyGuid && stateId != EmptyGuid && campusId != EmptyGuid)
    {
      setIsLoading(true);
    getIndianaStateBoardReportSettings(reportId, stateId, campusId)
      .then((res: any) => {
        if (!!res) {
          setSettings(res);
        } else {
          setToDefault();
        }
        setIsLoading(false);
      })
      .catch((e) => {
        setToDefault();
        setIsLoading(false);
      });
    }
    
  };

  const setToDefault = () => {
    setSettings(cloneDeep(initialModel));
  };

    React.useEffect(() => {
      if (
        props.campusId &&
        props.campusId !== EmptyGuid &&
        props.stateBoardReportId &&
        props.stateBoardReportId !== EmptyGuid &&
        props.stateId &&
        props.stateId !== EmptyGuid
      ) {
        getStateBoardReportSettings(
          props.stateBoardReportId,
          props.stateId,
          props.campusId
        );
      } else {
        setToDefault();
      }
    }, [props.campusId, props.stateBoardReportId, props.stateId]);

 const handleSubmit = () => {
    data.triggerValidation().then(validationResult => {
      if(validationResult == true && !!props.campusId && !!props.stateBoardReportId && !!props.stateId){
        setIsLoading(true);
        let settingsToPass = cloneDeep(settings);
        settingsToPass.campusId = props.campusId;
        settingsToPass.reportId = props.stateBoardReportId;
        settingsToPass.stateId = props.stateId;
        upsertIndianaStateBoardReportProgramCode(settingsToPass)
        .then(res => {
          setIsLoading(false);
          if(!!res && !!res.result && !!res.result.schoolStateBoardReportId){
            setSnackBarProps({
              showSnackBar: true,
              messageInfo: 'Saved Successfully',
              variant: "success",
            });
            getStateBoardReportSettings(
              props.stateBoardReportId,
              props.stateId,
              props.campusId
            );
          }else{
            setSnackBarProps({
              showSnackBar: true,
              messageInfo: 'Failed to save',
              variant: "error",
            });
          }
        })
        .catch(e => {
          setIsLoading(false);
        });
      }else{
        setSnackBarProps({
          showSnackBar: true,
          messageInfo: 'Please enter the required values.',
          variant: "error",
        });
      }
    });
  }

  const handleDelete = (
    campusId: string,
    reportId: string,
    stateId: string
  ) => {
    setOpenConfirmationPopup(true);
  };

  const performDelete = (
    campusId: string,
    reportId: string,
    stateId: string
  ) => {
    if (!!campusId && !!reportId && !!stateId) {
      setIsLoading(true);
      deleteIndianaStateBoardReportSettings(campusId, reportId, stateId)
        .then((res: any) => {
          setIsLoading(false);
          if (res == true) {
            setSnackBarProps({
              showSnackBar: true,
              messageInfo: "Deleted Successfully",
              variant: "success",
            });
            setToDefault();
            getStateBoardReportSettings(reportId, stateId, campusId);
          } else {
            setSnackBarProps({
              showSnackBar: true,
              messageInfo: "Failed to delete",
              variant: "error",
            });
          }
        })
        .catch((e: any) => {
          setIsLoading(false);
        });
    }
  };

  return (
    <>
      <CustomSnackbar
        variant={snackBarProps.variant}
        message={snackBarProps.messageInfo}
        open={snackBarProps.showSnackBar}
        onClose={(event?: React.SyntheticEvent, reason?: string) => {
          setSnackBarProps((props: any) => {
            return { ...props, showSnackBar: false };
          });
        }}
      ></CustomSnackbar>
      <div className={classes.wrapper}>
        <Grid
          container
          direction="row"
          justify="flex-start"
          alignItems="flex-start"
          spacing={1}
        >
          <Grid item xs={12} sm={12} md={12}>
            <ExpansionPanel style={{ marginTop: 10 }} defaultExpanded={true}>
              <ExpansionPanelSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
                className={classes.cardTitle}
                style={{ maxHeight: 20 }}
              >
                <Typography className={classes.heading}>
                  Campus Details
                </Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <Grid container direction="row" spacing={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography className={classes.titleMargin}>
                        Owner's name*
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography>
                        <InputField
                          id="ownerName"
                          name="ownerName"
                          type={InputType.TEXT}
                          className={classes.fieldMargin}
                          defaultValue={settings?.ownerName ?? ""}
                          error={!!data.errors["ownerName"]}
                          inputRef={data.register({
                            required: "Owner's name is required",
                          })}
                          helperText={
                            data.errors["ownerName"]
                              ? data.errors["ownerName"].message
                              : null
                          }
                          onBlur={(e: any) => {
                            handleChange("ownerName", e.target.value);
                          }}
                        />
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography className={classes.titleMargin}>
                        School name*
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography>
                        <InputField
                          id="schoolName"
                          name="schoolName"
                          type={InputType.TEXT}
                          className={classes.fieldMargin}
                          defaultValue={settings?.schoolName ?? ""}
                          error={!!data.errors["schoolName"]}
                          inputRef={data.register({
                            required: "School name is required",
                          })}
                          helperText={
                            data.errors["schoolName"]
                              ? data.errors["schoolName"].message
                              : null
                          }
                          onBlur={(e: any) => {
                            handleChange("schoolName", e.target.value);
                          }}
                        />
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography className={classes.titleMargin}>
                        School address 1*
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography>
                        <InputField
                          id="schoolAddress1"
                          name="schoolAddress1"
                          type={InputType.TEXT}
                          className={classes.fieldMargin}
                          defaultValue={settings?.schoolAddress1 ?? ""}
                          error={!!data.errors["schoolAddress1"]}
                          inputRef={data.register({
                            required: "School address is required",
                          })}
                          helperText={
                            data.errors["schoolAddress1"]
                              ? data.errors["schoolAddress1"].message
                              : null
                          }
                          onBlur={(e: any) => {
                            handleChange("schoolAddress1", e.target.value);
                          }}
                        />
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography className={classes.titleMargin}>
                        School address 2
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography>
                        <InputField
                          id="schoolAddress2"
                          name="schoolAddress2"
                          type={InputType.TEXT}
                          className={classes.fieldMargin}
                          defaultValue={settings?.schoolAddress2 ?? ""}
                          onBlur={(e: any) => {
                            handleChange("schoolAddress2", e.target.value);
                          }}
                        />
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography className={classes.titleMargin}>
                        City *
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography>
                        <InputField
                          id="campusCity"
                          name="campusCity"
                          type={InputType.TEXT}
                          className={classes.fieldMargin}
                          defaultValue={settings?.campusCity ?? ""}
                          error={!!data.errors["campusCity"]}
                          inputRef={data.register({
                            required: "City is required",
                          })}
                          helperText={
                            data.errors["campusCity"]
                              ? data.errors["campusCity"].message
                              : null
                          }
                          onBlur={(e: any) => {
                            handleChange("campusCity", e.target.value);
                          }}
                        />
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography className={classes.titleMargin}>
                        Country *
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <CountryAutoComplete
                        filterHandle={(v: any) => {
                          handleChange(
                            "campusCountryId",
                            v ? v.value : undefined
                          );
                        }}
                        params={{ fullWidth: true }}
                        id="campusCountryId"
                        name="campusCountryId"
                        label=" "
                        valueFilter={
                          settings &&
                          settings.campusCountryId &&
                          settings.campusCountryId != EmptyGuid
                            ? {
                                key: "value",
                                values: [
                                  settings.campusCountryId.toLowerCase(),
                                ],
                              }
                            : undefined
                        }
                        campusId={props.campusId}
                        countryName="USA"
                        inputRef={data.register({
                          required: "Country is required",
                        })}
                        helperText={
                          !!data.errors["campusCountryId"]
                            ? data.errors["campusCountryId"].message
                            : null
                        }
                        error={!!data.errors["campusCountryId"]}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography className={classes.titleMargin}>
                        State *
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <StatesAutoComplete
                        id="campusStateId"
                        name="campusStateId"
                        label={" "}
                        params={{ fullWidth: true }}
                        countryId={settings.campusCountryId}
                        valueFilter={
                          settings.campusStateId &&
                          settings.campusStateId != EmptyGuid
                            ? {
                                key: "value",
                                values: [settings.campusStateId.toLowerCase()],
                              }
                            : undefined
                        }
                        filterHandle={(v: any) => {
                          handleChange(
                            "campusStateId",
                            v ? v.value : undefined
                          );
                        }}
                        inputRef={data.register({
                          required: "State is required",
                        })}
                        helperText={
                          data.errors["campusStateId"]
                            ? data.errors["campusStateId"].message
                            : null
                        }
                        error={!!data.errors.campusStateId}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography className={classes.titleMargin}>
                        Zipcode *
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography>
                        <InputField
                          id="campusZipCode"
                          name="campusZipCode"
                          type={InputType.TEXT}
                          className={classes.fieldMargin}
                          defaultValue={settings?.campusZipCode ?? ""}
                          error={!!data.errors["campusZipCode"]}
                          inputRef={data.register({
                            required: "Zipcode is required",
                          })}
                          helperText={
                            data.errors["campusZipCode"]
                              ? data.errors["campusZipCode"].message
                              : null
                          }
                          onBlur={(e: any) => {
                            handleChange("campusZipCode", e.target.value);
                          }}
                        />
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography className={classes.titleMargin}>
                        Owner's licensing number*
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography>
                        <InputField
                          id="ownerLicenseNumber"
                          name="ownerLicenseNumber"
                          type={InputType.TEXT}
                          className={classes.fieldMargin}
                          defaultValue={settings?.ownerLicenseNumber ?? ""}
                          error={!!data.errors["ownerLicenseNumber"]}
                          inputRef={data.register({
                            required: "Owner's licensing number is required",
                          })}
                          helperText={
                            data.errors["ownerLicenseNumber"]
                              ? data.errors["ownerLicenseNumber"].message
                              : null
                          }
                          onBlur={(e: any) => {
                            handleChange("ownerLicenseNumber", e.target.value);
                          }}
                        />
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography className={classes.titleMargin}>
                        Officer's name 1
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography>
                        <InputField
                          id="officerName1"
                          name="officerName1"
                          type={InputType.TEXT}
                          className={classes.fieldMargin}
                          defaultValue={settings?.officerName1 ?? ""}
                          onBlur={(e: any) => {
                            handleChange("officerName1", e.target.value);
                          }}
                        />
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography className={classes.titleMargin}>
                        Officer's name 2
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography>
                        <InputField
                          id="officerName2"
                          name="officerName2"
                          type={InputType.TEXT}
                          className={classes.fieldMargin}
                          defaultValue={settings?.officerName2 ?? ""}
                          onBlur={(e: any) => {
                            handleChange("officerName2", e.target.value);
                          }}
                        />
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography className={classes.titleMargin}>
                        Officer's name 3
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography>
                        <InputField
                          id="officerName3"
                          name="officerName3"
                          type={InputType.TEXT}
                          className={classes.fieldMargin}
                          defaultValue={settings?.officerName3 ?? ""}
                          onBlur={(e: any) => {
                            handleChange("officerName3", e.target.value);
                          }}
                        />
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </ExpansionPanelDetails>
            </ExpansionPanel>

            <ExpansionPanel style={{ marginTop: 10 }} defaultExpanded={true}>
              <ExpansionPanelSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
                className={classes.cardTitle}
                style={{ maxHeight: 20 }}
              >
                <Typography className={classes.heading}>
                  State Board Licensing Agency Details
                </Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <Grid container direction="row" spacing={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography
                        className={classes.licensingAgencyTitleMargin}
                      >
                        Licensing agency *
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <LicensingAgencyAutoComplete
                        id="licensingAgencyId"
                        name="licensingAgencyId"
                        label={""}
                        stateId={props.stateId}
                        valueFilter={
                          settings.licensingAgencyId
                            ? {
                                key: "value",
                                values: [settings.licensingAgencyId],
                              }
                            : undefined
                        }
                        filterHandle={(v: any) => {
                          handleChange(
                            "licensingAgencyId",
                            v ? v.value : undefined
                          );
                        }}
                        inputRef={data.register({
                          required: "Licensing agency is required",
                        })}
                        helperText={
                          data.errors["licensingAgencyId"]
                            ? data.errors["licensingAgencyId"].message
                            : null
                        }
                        error={!!data.errors.licensingAgencyId}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography className={classes.titleMargin}>
                        Licensing agency address 1 *
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography>
                        <InputField
                          id="licensingAddress1"
                          name="licensingAddress1"
                          type={InputType.TEXT}
                          className={classes.fieldMargin}
                          defaultValue={settings?.licensingAddress1 ?? ""}
                          error={!!data.errors["licensingAddress1"]}
                          inputRef={data.register({
                            required: "Address is required",
                          })}
                          helperText={
                            data.errors["licensingAddress1"]
                              ? data.errors["licensingAddress1"].message
                              : null
                          }
                          onBlur={(e: any) => {
                            handleChange("licensingAddress1", e.target.value);
                          }}
                        />
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography className={classes.titleMargin}>
                        Licensing agency address 2
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography>
                        <InputField
                          id="licensingAddress2"
                          name="licensingAddress2"
                          type={InputType.TEXT}
                          className={classes.fieldMargin}
                          defaultValue={settings?.licensingAddress2 ?? ""}
                          onBlur={(e: any) => {
                            handleChange("licensingAddress2", e.target.value);
                          }}
                        />
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography className={classes.titleMargin}>
                        Country *
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <CountryAutoComplete
                        filterHandle={(v: any) => {
                          handleChange(
                            "licensingCountryId",
                            v ? v.value : undefined
                          );
                        }}
                        id="licensingCountryId"
                        name="licensingCountryId"
                        label=" "
                        valueFilter={
                          settings &&
                          settings.licensingCountryId &&
                          settings.licensingCountryId != EmptyGuid
                            ? {
                                key: "value",
                                values: [
                                  settings.licensingCountryId.toLowerCase(),
                                ],
                              }
                            : undefined
                        }
                        campusId={props.campusId}
                        countryName="USA"
                        inputRef={data.register({
                          required: "Country is required",
                        })}
                        helperText={
                          !!data.errors["licensingCountryId"]
                            ? data.errors["licensingCountryId"].message
                            : null
                        }
                        error={!!data.errors["licensingCountryId"]}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography className={classes.titleMargin}>
                        City *
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography>
                        <InputField
                          id="licensingCity"
                          name="licensingCity"
                          type={InputType.TEXT}
                          className={classes.fieldMargin}
                          defaultValue={settings?.licensingCity ?? ""}
                          error={!!data.errors["licensingCity"]}
                          inputRef={data.register({
                            required: "City is required",
                          })}
                          helperText={
                            data.errors["licensingCity"]
                              ? data.errors["licensingCity"].message
                              : null
                          }
                          onBlur={(e: any) => {
                            handleChange("licensingCity", e.target.value);
                          }}
                        />
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography className={classes.titleMargin}>
                        State *
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <AllStatesAutoComplete
                        id="licensingStateId"
                        name="licensingStateId"
                        label={" "}
                        valueFilter={
                          settings.licensingStateId &&
                          settings.licensingStateId != EmptyGuid
                            ? {
                                key: "value",
                                values: [
                                  settings.licensingStateId.toLowerCase(),
                                ],
                              }
                            : undefined
                        }
                        filterHandle={(v: any) => {
                          handleChange(
                            "licensingStateId",
                            v ? v.value : undefined
                          );
                        }}
                        inputRef={data.register({
                          required: "State is required",
                        })}
                        helperText={
                          data.errors["licensingStateId"]
                            ? data.errors["licensingStateId"].message
                            : null
                        }
                        error={!!data.errors.licensingStateId}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography className={classes.titleMargin}>
                        Zipcode *
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <InputField
                        id="licensingZipCode"
                        name="licensingZipCode"
                        type={InputType.TEXT}
                        className={classes.fieldMargin}
                        defaultValue={settings?.licensingZipCode ?? ""}
                        error={!!data.errors["licensingZipCode"]}
                        inputRef={data.register({
                          required: "Zipcode is required",
                        })}
                        helperText={
                          data.errors["licensingZipCode"]
                            ? data.errors["licensingZipCode"].message
                            : null
                        }
                        onBlur={(e: any) => {
                          handleChange("licensingZipCode", e.target.value);
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </ExpansionPanelDetails>
            </ExpansionPanel>

            <ExpansionPanel style={{ marginTop: 10 }} defaultExpanded={true}>
              <ExpansionPanelSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
                className={classes.cardTitle}
                style={{ maxHeight: 20 }}
              >
                <Typography className={classes.heading}>
                  Campus Program Mapping to Licensing Agency Course Code
                </Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <Grid container direction="row" spacing={2}>
                  <Table
                    //aria-labelledby="tableTitle"
                    //size="medium"
                    aria-label="sticky table"
                    stickyHeader
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell
                          className={classes.headerFont}
                          style={{ width: "60%" }}
                        >
                          Program Name
                        </TableCell>
                        <TableCell className={classes.headerFont}>
                          Program Code
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!!settings?.stateBoardProgramMappings &&
                        !isLoading &&
                        settings?.stateBoardProgramMappings.length >
                          0 &&
                        settings?.stateBoardProgramMappings.map(
                          (row) => (
                            <TableRow key={`${row.programId}`}>
                              <TableCell>{row.programDescription}</TableCell>
                              <TableCell>
                                {/* <InputField
                                  type={InputType.TEXT}
                                  key={`txt${row.progId}}`}
                                  id={`txt_${row.progId}`}
                                  label="Program Code"
                                  name="programCode"
                                  defaultValue={row?.programCode}
                                  onBlur={(
                                    e: React.FocusEvent<HTMLInputElement>
                                  ) => {
                                    handleProgramCodeChange(
                                      e.target.value,
                                      row.progId
                                    );
                                  }}
                                /> */}
                                <StateBoardCoursesAutoComplete  
                                  key={`stateBoardCourseId_${row.programId}`}
                                  id={`stateBoardCourseId_${row.programId}`}
                                  name="stateBoardCourseId"
                                  params={{ fullWidth: true }}
                                  campusId={props.campusId}
                                  stateId={props.stateId}
                                  valueFilter={
                                    row.stateBoardCourseId
                                      ? {
                                          key: "value",
                                          values: [
                                            row.stateBoardCourseId,
                                          ],
                                        }
                                      : undefined
                                  }
                                  filterHandle={(v: any) => {
                                    handleProgramCodeChange(
                                      v ? v.value : undefined,
                                      row.programId,
                                      props.stateBoardReportId
                                    );
                                  }}
                                  />
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      {isLoading && (
                        <TableRow>
                          <TableCell
                            style={{ textAlign: "center" }}
                            colSpan={2}
                          >
                            <CircularProgress />
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Grid>
              </ExpansionPanelDetails>
            </ExpansionPanel>
          </Grid>
        </Grid>
      </div>
      <div className={classes.componentActions}>
        <ProgressSaveButton
          key="btnSaveIndianaProgramCodeSettings"
          text="Save"
          onClick={handleSubmit}
          loading={isLoading}
          disabled={
            !settings?.stateBoardProgramMappings ||
            settings?.stateBoardProgramMappings.length <= 0
          }
          size="small"
        ></ProgressSaveButton>
        <ProgressSaveButton
          key={"btnDeleteMichiganStateBoardSettings"}
          text="Delete"
          onClick={handleDelete}
          disabled={settings.schoolStateBoardReportId == EmptyGuid}
          loading={isLoading}
          size="small"
          buttonClassName={classes.btnDelete}
        />
      </div>
      <Dialog
        disableBackdropClick
        disableEscapeKeyDown
        maxWidth="xs"
        aria-labelledby="confirmation-dialog"
        open={openConfirmationPopup}
        onClose={() => {}}
      >
        <DialogContent>
          <Typography>Are you sure you want to delete this setting?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              performDelete(
                props.campusId,
                props.stateBoardReportId,
                props.stateId
              );
              setOpenConfirmationPopup(false);
            }}
            color="primary"
          >
            {"YES"}
          </Button>
          <Button
            autoFocus
            onClick={() => {
              setOpenConfirmationPopup(false);
            }}
            color="primary"
          >
            NO
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IndianaStateBoardReportSetup;
